// [SERVICE] Credits API Service - Système AI Credits
// [RÔLE] Gestion des appels API pour balance, historique, stats de crédits AI
// [ARCHITECTURE] Microservice communication avec famla-api:9540/api/v1/credits

import  FamlaServices  from "./FamlaServices";

/**
 * Service pour gérer les crédits AI du système de facturation
 * Communique avec les endpoints /api/v1/credits/*
 */
class CreditsService {
    /**
     * Récupère la balance de crédits AI pour une company
     * @param companyId - ID de la company
     * @param accessToken - Token JWT d'authentification
     * @returns Balance détaillée des crédits
     */
    static async getCreditBalance(companyId: string, accessToken: string) {
        try {
            const response = await FamlaServices(
                "api_host",
                `/credits/balance/${companyId}`,
                "GET",
                "",
                accessToken
            );

            if (response.status === 200) {
                return {
                    success: true,
                    data: response.body.data,
                };
            } else if (response.status === 401) {
                return {
                    success: false,
                    error: "Unauthorized",
                    message: response.body.description || "Authentication required",
                };
            } else {
                return {
                    success: false,
                    error: "API Error",
                    message: response.body.description || "Failed to fetch credit balance",
                };
            }
        } catch (error) {
            console.error("[CreditsService] getCreditBalance error:", error);
            return {
                success: false,
                error: "Network Error",
                message: error instanceof Error ? error.message : "Unknown error",
            };
        }
    }

    /**
     * Récupère l'historique des transactions de crédits
     * @param companyId - ID de la company
     * @param accessToken - Token JWT d'authentification
     * @param limit - Nombre maximum de transactions à récupérer (défaut: 100)
     * @param skip - Nombre de transactions à sauter (pagination)
     * @returns Historique des transactions
     */
    static async getCreditHistory(
        companyId: string,
        accessToken: string,
        limit: number = 100,
        skip: number = 0
    ) {
        try {
            const response = await FamlaServices(
                "api_host",
                `/credits/history/${companyId}?limit=${limit}&skip=${skip}`,
                "GET",
                "",
                accessToken
            );

            if (response.status === 200) {
                return {
                    success: true,
                    data: response.body.data,
                };
            } else if (response.status === 401) {
                return {
                    success: false,
                    error: "Unauthorized",
                    message: response.body.description || "Authentication required",
                };
            } else {
                return {
                    success: false,
                    error: "API Error",
                    message: response.body.description || "Failed to fetch credit history",
                };
            }
        } catch (error) {
            console.error("[CreditsService] getCreditHistory error:", error);
            return {
                success: false,
                error: "Network Error",
                message: error instanceof Error ? error.message : "Unknown error",
            };
        }
    }

    /**
     * Récupère les statistiques de consommation de crédits
     * @param companyId - ID de la company
     * @param accessToken - Token JWT d'authentification
     * @returns Statistiques détaillées
     */
    static async getCreditStats(companyId: string, accessToken: string) {
        try {
            const response = await FamlaServices(
                "api_host",
                `/credits/stats/${companyId}`,
                "GET",
                "",
                accessToken
            );

            if (response.status === 200) {
                return {
                    success: true,
                    data: response.body.data,
                };
            } else if (response.status === 401) {
                return {
                    success: false,
                    error: "Unauthorized",
                    message: response.body.description || "Authentication required",
                };
            } else {
                return {
                    success: false,
                    error: "API Error",
                    message: response.body.description || "Failed to fetch credit stats",
                };
            }
        } catch (error) {
            console.error("[CreditsService] getCreditStats error:", error);
            return {
                success: false,
                error: "Network Error",
                message: error instanceof Error ? error.message : "Unknown error",
            };
        }
    }

    /**
     * Récupère la configuration système (prix, crédits, etc.)
     * @param accessToken - Token JWT d'authentification
     * @returns Configuration système pour affichage Pricing
     */
    static async getSystemConfig(accessToken: string) {
        try {
            const response = await FamlaServices(
                "api_host",
                `/credits/config/system`,
                "GET",
                "",
                accessToken
            );

            if (response.status === 200) {
                return {
                    success: true,
                    data: response.body.data,
                };
            } else if (response.status === 401) {
                return {
                    success: false,
                    error: "Unauthorized",
                    message: response.body.description || "Authentication required",
                };
            } else {
                return {
                    success: false,
                    error: "API Error",
                    message: response.body.description || "Failed to fetch system config",
                };
            }
        } catch (error) {
            console.error("[CreditsService] getSystemConfig error:", error);
            return {
                success: false,
                error: "Network Error",
                message: error instanceof Error ? error.message : "Unknown error",
            };
        }
    }

    /**
     * Formatte le nom du plan pour affichage UI
     * @param plan - Nom du plan (FREE, ESSENTIAL, PLUS, ENTERPRISE)
     * @returns Nom formatté avec emoji
     */
    static formatPlanName(plan: string): string {
        const planMap: Record<string, string> = {
            FREE: "🆓 Free",
            ESSENTIAL: "⭐ Essential",
            PLUS: "💎 Plus",
            ENTERPRISE: "🏢 Enterprise",
        };
        return planMap[plan] || plan;
    }

    /**
     * Calcule le pourcentage de crédits utilisés
     * @param creditsAvailable - Crédits disponibles
     * @param creditsQuota - Quota total de crédits
     * @returns Pourcentage utilisé (0-100)
     */
    static calculateUsagePercentage(creditsAvailable: number, creditsQuota: number): number {
        if (creditsQuota === 0) return 0;
        const used = creditsQuota - creditsAvailable;
        return Math.round((used / creditsQuota) * 100);
    }

    /**
     * Détermine la couleur de la barre de progression selon le pourcentage
     * @param percentage - Pourcentage utilisé
     * @returns Classe CSS Bootstrap pour la couleur
     */
    static getProgressBarColor(percentage: number): string {
        if (percentage >= 90) return "bg-danger";
        if (percentage >= 70) return "bg-warning";
        return "bg-success";
    }

    /**
     * Vérifie si un plan peut faire des top-ups
     * @param plan - Plan actuel (FREE, ESSENTIAL, PLUS, ENTERPRISE)
     * @returns true si le top-up est disponible
     * 
     * ✅ ESSENTIAL : Top-up standard (acheter plus de crédits)
     * ✅ PLUS : Top-up d'urgence (si crédits mensuels épuisés avant renouvellement)
     */
    static canTopUp(plan: string): boolean {
        return plan === 'ESSENTIAL' || plan === 'PLUS';
    }

    /**
     * Détermine le message à afficher selon le statut des crédits
     * @param plan - Plan actuel
     * @param creditsAvailable - Crédits disponibles
     * @returns Message formaté pour l'UI
     */
    static getCreditStatusMessage(plan: string, creditsAvailable: number): { message: string; type: string } {
        if (plan === 'FREE' && creditsAvailable <= 0) {
            return {
                message: 'Mode Consultation - Ajoutez des crédits pour créer de nouveaux processus',
                type: 'info'
            };
        }
        if (plan === 'ENTERPRISE' && creditsAvailable <= 0) {
            return {
                message: 'Crédits épuisés - Veuillez contacter votre administrateur',
                type: 'warning'
            };
        }
        if (creditsAvailable <= 10) {
            return {
                message: 'Crédits bientôt épuisés - Pensez à recharger',
                type: 'warning'
            };
        }
        return {
            message: `${creditsAvailable} crédits disponibles`,
            type: 'success'
        };
    }
}

export default CreditsService;

